/**
 * SOLOMON — MERMAID SYNTAX NORMALIZATION
 *
 * Extracted from DiagramBlock.jsx so it can be unit-tested: that file imports
 * React and mermaid, so `node --test` cannot load it. This module is pure —
 * string in, string out, zero imports.
 *
 * These functions repair common AI-generated Mermaid defects before render.
 * They are also the thing most likely to CAUSE one: they rewrite syntax that
 * was already valid. See mermaidNormalize.test.js.
 */

/**
 * Normalize Mermaid syntax to fix common AI-generated issues.
 * 1. Strip %%{init:} directives and useHtmlLabels overrides
 * 2. Fix unicode characters (smart quotes, em dashes, zero-width)
 * 3. Quote-wrap node labels to prevent special chars from breaking parsing
 */
export function normalizeSyntax(raw) {
  let result = raw
    // Strip %%{init: ... }%% directives — prevents AI from overriding
    // our config (e.g., re-enabling htmlLabels: true)
    // Uses [\s\S] to handle multi-line directives with nested braces
    .replace(/%%\s*\{[\s\S]*?\}%%/g, '')
    // Strip any useHtmlLabels references in raw syntax
    .replace(/useHtmlLabels\s*:\s*(true|false)/gi, '')
    .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')   // smart double quotes → "
    .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")   // smart single quotes → '
    .replace(/[\u2013\u2014]/g, '--')                           // en/em dashes → --
    // eslint-disable-next-line no-misleading-character-class -- standalone zero-width code points, intentional sanitization
    .replace(/[\u200B\u200C\u200D\uFEFF]/g, '')                // zero-width chars
    .replace(/[\uE000\uE001]/g, '')                             // PUA — reserved as the quote-masking sentinel below
    .replace(/\u00A0/g, ' ');                                   // non-breaking space → space

  // Quote-wrap node labels to prevent special characters from breaking
  // Mermaid's parser (parentheses, ampersands, colons, etc.)
  result = quoteWrapLabels(result);

  return result;
}

/**
 * Quote-wrap all node labels in flowchart/graph diagrams.
 *
 * Mermaid treats special characters inside node labels as syntax:
 *   A[Get Contract (Agreement)] → parse error (inner parens)
 *   A["Get Contract (Agreement)"] → works (quoted literal)
 *
 * This function finds node definitions and wraps their labels in quotes
 * if they aren't already quoted and contain potentially breaking chars.
 *
 * Handles: [label], (label), {label}, ([label]), [[label]], [(label)],
 *          ((label)), {{label}}, >label], and |edge labels|
 */
function quoteWrapLabels(syntax) {
  // Only apply to flowchart/graph diagrams
  const firstLine = syntax.trim().split('\n')[0].trim().toLowerCase();
  const isFlowchart = /^(graph|flowchart)\s/i.test(firstLine);
  if (!isFlowchart) return syntax;

  return syntax.split('\n').map(line => {
    // Skip comment lines, directives, and empty lines
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('%%') || trimmed.startsWith('style ') || trimmed.startsWith('classDef ') || trimmed.startsWith('class ')) {
      return line;
    }

    // ── Mask already-quoted spans BEFORE any rewriting ──────────────────
    // Every replacer below scans raw text, and none of them can distinguish
    // "inside an already-quoted label" from "syntax". That is the whole bug
    // class fixed here (2026-08-20): a valid label `C["Flips<br><i>x</i>"]`
    // handed the asymmetric-node replacer /(\w+)>(?!")([^\]]*)\]/ a `br>` to
    // match on, so it read `br` as a node id and rewrote the middle of the
    // label into `C["Flips<br>"<i>x</i>'"]` — which mermaid rejects with
    // "got 'STR'". The per-replacer `label.startsWith('"')` guard could never
    // catch it, because the captured label began at `<i>`, not at a quote.
    //
    // Masking once, here — the only place that sees the whole line — fixes it
    // for EVERY replacer instead of the one that happened to fire. The sentinel
    // is PUA-delimited digits (U+E000/U+E001 — Private Use: reserved for
    // private use, so we claim it, and strip it from input first). It contains
    // no character in BREAKING_CHARS, so a masked label reads as "nothing to
    // fix" and passes through untouched.
    //
    // TWO RULES, and the first cut of this fix got the second one wrong:
    //   1. the DECISION to wrap is made on the MASKED label — "does the
    //      UNQUOTED portion contain breaking chars?" is exactly the right
    //      question, and it is what makes an already-quoted label a no-op.
    //   2. the TEXT that gets wrapped must be UNMASKED FIRST, so each wrapper's
    //      own `"` -> `'` sanitizer can still see real quotes. Skipping that
    //      restored raw quotes INSIDE the quotes the wrapper had just added —
    //      `A[Send "offer" (x)]` became `A["Send "offer" (x)"]`, re-creating
    //      this very bug from the opposite direction (caught in review).
    //
    // Masking is skipped entirely on a line with an ODD number of quotes: the
    // pairing would span a non-label region and rewrite a valid label further
    // along the line. Such a line is malformed anyway, so it takes the
    // pre-masking path rather than a confidently-wrong one.
    const quoted = [];
    const balancedQuotes = ((line.match(/"/g) || []).length % 2) === 0;
    if (balancedQuotes) {
      line = line.replace(/"[^"]*"/g, (m) => {
        quoted.push(m);
        return `\uE000${quoted.length - 1}\uE001`;
      });
    }
    const unmask = (t) => t.replace(/\uE000(\d+)\uE001/g, (_, i) => quoted[Number(i)]);

    // Quote-wrap subgraph titles: subgraph Title Text Here → subgraph "Title Text Here"
    // The id[title] declaration form (`subgraph Browser["Browser Window"]` /
    // `subgraph Browser[Browser Window]`) is NOT a bare title and must fall
    // through to the node-label machinery below, which already handles the
    // bracket content (masked-decision + wrap). Treating it as a title
    // re-wrapped the whole declaration — the mask token's brackets tripped
    // the breaking-char test — producing nested quotes and a parse error
    // (prod regression, 2026-08-26).
    line = line.replace(/^(\s*subgraph\s+)(?!")(?![\w-]+\s*\[)(.*\S.*)$/m, (_, prefix, title) => {
      if (/[()&;|<>{}[\]:]/.test(title)) {
        return `${prefix}"${unmask(title).replace(/"/g, "'")}"`;
      }
      return _;
    });

    // Spaced bracket form: `subgraph s1 [Phase (one)]`. The guard above
    // exempts it from title-wrapping, but the node-label replacer requires
    // id[ with NO space — so wrap the bracket label here (quoted labels are
    // masked and no-op; the (?!["[(]) mirrors the node-shape delimiters).
    line = line.replace(/^(\s*subgraph\s+[\w-]+\s+)\[(?!["[(])([^\]]*)\]/, (m, pre, label) =>
      /[()&;|<>{}:]/.test(label) ? `${pre}["${unmask(label).replace(/"/g, "'")}"]` : m);

    // Process all node label patterns on this line.
    // Match: nodeId + delimiter + label + closing delimiter
    // Patterns: [label], (label), {label}, ([label]), [[label]], [(label)], ((label)), {{label}}, >label]
    line = quoteWrapNodeLabels(line, unmask);

    // Quote-wrap edge labels: -->|label text| or -- label text -->
    line = line.replace(/\|([^|"]+)\|/g, (match, label) => {
      if (/[()&;:<>{}[\]]/.test(label)) {
        return `|"${unmask(label).replace(/"/g, "'")}"|`;
      }
      return match;
    });

    // Restore the quoted spans byte-for-byte.
    line = line.replace(/\uE000(\d+)\uE001/g, (_, i) => quoted[Number(i)]);

    return line;
  }).join('\n');
}

/**
 * Find and quote-wrap node labels within a single line.
 * Handles all Mermaid node shape delimiters.
 */
function quoteWrapNodeLabels(line, unmask) {
  // Characters that break Mermaid parsing inside labels
  const BREAKING_CHARS = /[()&;|<>[\]{}]/;

  // Strategy: find node definitions by scanning for nodeId + opening delimiter,
  // then extract the label by counting balanced delimiters to find the true close.
  // This handles nested brackets like A[Error Handler [fallback]] correctly.

  // Process multi-char delimiters first, then single-char.
  // Each replacement is idempotent — already-quoted labels are skipped.
  const replacers = [
    // ([label]) — stadium
    { re: /(\w+)\(\[(?!")(.*?)\]\)/g, open: '([', close: '])' },
    // [[label]] — subroutine
    { re: /(\w+)\[\[(?!")(.*?)\]\]/g, open: '[[', close: ']]' },
    // [(label)] — cylinder
    { re: /(\w+)\[\((?!")(.*?)\)\]/g, open: '[(', close: ')]' },
    // ((label)) — circle
    { re: /(\w+)\(\((?!")(.*?)\)\)/g, open: '((', close: '))' },
    // {{label}} — hexagon
    { re: /(\w+)\{\{(?!")(.*?)\}\}/g, open: '{{', close: '}}' },
    // {label} — diamond (must come before single-char catch-alls)
    { re: /(\w+)\{(?![{"])([^}]*)\}/g, open: '{', close: '}' },
    // (label) — rounded (skip if followed by [ which means stadium)
    { re: /(\w+)\((?![("[\]])([^)]*)\)/g, open: '(', close: ')' },
    // >label] — asymmetric
    { re: /(\w+)>(?!")([^\]]*)\]/g, open: '>', close: ']' },
  ];

  for (const { re, open, close } of replacers) {
    line = line.replace(re, (match, nodeId, label) => {
      if (label.startsWith('"') || !BREAKING_CHARS.test(label)) return match;
      const safeLabel = unmask(label).replace(/"/g, "'");
      return `${nodeId}${open}"${safeLabel}"${close}`;
    });
  }

  // [label] — rectangle (most common, handled last and separately)
  // Use a smarter approach: find nodeId[ then scan forward for the matching ]
  // that is NOT followed by ] (which would be part of [[ ]])
  line = line.replace(/(\w+)\[(?![[("])([^\]]*)\]/g, (match, nodeId, label) => {
    if (label.startsWith('"') || !BREAKING_CHARS.test(label)) return match;
    const safeLabel = unmask(label).replace(/"/g, "'");
    return `${nodeId}["${safeLabel}"]`;
  });

  return line;
}
